import { SlideshowSelectEvent, ThemeEvents } from '@theme/events';

/**
 * Rocky PDP gallery — land on the pack shot after a variant change.
 *
 * On first paint the gallery opens on slide 1, a lifestyle shot, with the
 * selected variant's plain-background pack shot inset on slide 2 (the "show the
 * first image second" reorder in product-media-gallery-content.liquid). That is
 * the right opening, but it makes a variant change invisible: media-gallery.js
 * answers `variant:update` by swapping in the freshly rendered <media-gallery>
 * from the response, and that new slideshow boots on slide 1 again — the same
 * lifestyle shot the shopper was already looking at, with the new size's jar a
 * swipe away.
 *
 * This module runs AFTER that swap. `VariantUpdateEvent` bubbles from the
 * <variant-picker>; media-gallery listens on the enclosing `.shopify-section`
 * and replaces itself synchronously, so by the time the event reaches a
 * bubble-phase listener on `document` the new gallery is connected and its
 * slideshow has booted. The module finds the pack shot slide (the
 * `r-media-gallery__slide--packshot` marker, stamped server-side and keyed on
 * the selected variant's featured media) and calls the slideshow's public
 * `select(index)` with animation off before the new gallery has painted, then
 * holds that position through the engine's boot (see `landOnPackshot`). The engine does the rest: an instant
 * scroll of the track, `aria-hidden`, and the current mark on its own controls
 * (dots, thumbnails, counter). Nothing here scrolls anything itself.
 *
 * Two engine paths that look like the obvious tool were tried and do not work
 * here, recorded so nobody reaches for them again:
 *   - `initial-slide` on the response document before the swap: the boot sets
 *     `current` to that index and then select()s it, and select() treats
 *     "already current" as a no-op — thumbnail marked, track still on slide 1.
 *   - `initial-slide` on the connected element (the post-morph path collection
 *     filtering uses): it selects by `slide-id`, and the product gallery's
 *     slides carry none, so it finds nothing and returns.
 *
 * Initial page load is deliberately untouched: the server renders
 * `initial-slide="0"` and no event fires, so a shared `?variant=` link still
 * opens on the lifestyle shot.
 *
 * Composition over the engine: events.js, media-gallery.js and slideshow.js
 * are all read, none patched.
 */

const PACKSHOT_CLASS = 'r-media-gallery__slide--packshot';

/** Frames to wait for the engine's intersection observer before standing down (~1 s). */
const MAX_FRAMES = 60;

function landOnPackshot() {
  const slideshow = document.querySelector('.r-pdp-gallery media-gallery slideshow-component[ref="slideshow"]');
  const track = slideshow?.querySelector('slideshow-slides');
  if (!slideshow || !track || typeof slideshow.select !== 'function') return;

  const slides = Array.from(track.children).filter((el) => el.tagName === 'SLIDESHOW-SLIDE');
  const index = slides.findIndex((slide) => slide.classList.contains(PACKSHOT_CLASS));
  if (index <= 0) return;

  const land = () => slideshow.select(index, undefined, { animate: false });

  // 1. Land NOW, in the same task as the swap, so the first paint of the new
  //    gallery is already the pack shot. No frame ever shows slide 1.
  land();

  // 2. Hold it through the engine's boot. The boot ends with a deferred step
  //    (scheduler → setTimeout → rAF) that reads its IntersectionObserver's
  //    list of visible slides and, if that list is still empty, select()s
  //    slide 0 — instantly, which would flash the lifestyle shot. That select
  //    dispatches `slideshow:select` synchronously, so answering it with
  //    another instant select in the same task puts the track back before
  //    the frame paints: two scrolls, one paint, nothing visible. The guard is
  //    exact — it only acts while the observer has reported nothing, which is
  //    the one condition under which the boot selects 0 — so a shopper's own
  //    swipe (which cannot happen before the observer reports) is never
  //    bounced. It stands down as soon as the observer has reported, or after
  //    MAX_FRAMES for a gallery that never does (off-screen, hidden tab).
  const settled = () => (slideshow.visibleSlides?.length ?? 0) > 0;

  /** @param {Event & { detail?: { index?: number } }} event */
  const hold = (event) => {
    if (event.target !== slideshow || settled()) return;
    if (event.detail?.index !== index) land();
  };
  slideshow.addEventListener(SlideshowSelectEvent.eventName, hold);

  let frames = 0;
  const tick = () => {
    if (!slideshow.isConnected) return slideshow.removeEventListener(SlideshowSelectEvent.eventName, hold);
    if (settled()) {
      // Boot deferred behind a view transition: the immediate land was a no-op.
      if (slideshow.current === 0) land();
      return slideshow.removeEventListener(SlideshowSelectEvent.eventName, hold);
    }
    if (++frames < MAX_FRAMES) requestAnimationFrame(tick);
    else slideshow.removeEventListener(SlideshowSelectEvent.eventName, hold);
  };
  requestAnimationFrame(tick);
}

document.addEventListener(ThemeEvents.variantUpdate, landOnPackshot);
